import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Users, Wallet, TrendingUp, FileText, UserPlus } from 'lucide-react';
import { formatCurrency, formatCompactCurrency, custodianAPI, custodianCouponPaymentsAPI } from '../../services/api';
import { useQuery } from '@tanstack/react-query';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const CustodianDashboard = () => {
  // Fetch sub-investors data
  const { data: subInvestorsResponse, isLoading: subInvestorsLoading, error: subInvestorsError } = useQuery({
    queryKey: ['custodian-sub-investors', 'dashboard'],
    queryFn: () => custodianAPI.getSubInvestors({ page: 1, limit: 1000 }),
    select: (response) => {
      const subInvestors = response?.data?.subInvestors || [];
      return subInvestors;
    }
  });

  // Fetch custodian's own wallets data
  const { data: walletsResponse, isLoading: walletsLoading, error: walletsError } = useQuery({
    queryKey: ['custodian-my-wallets', 'dashboard'],
    queryFn: () => custodianAPI.getMyWallets(),
    select: (response) => {
      const wallets = response?.data?.wallets || [];
      return wallets;
    }
  });

  // Fetch custodian coupon/profit distribution payments data
  const { data: paymentsResponse, isLoading: paymentsLoading, error: paymentsError } = useQuery({
    queryKey: ['custodian-coupon-payments', 'dashboard'],
    queryFn: () => custodianCouponPaymentsAPI.getPayments(),
    select: (response) => {
      const payments = response?.data?.data || response?.data || [];
      return Array.isArray(payments) ? payments : [];
    }
  });

  // Calculate active sub-investors count
  const activeSubInvestorsCount = React.useMemo(() => {
    if (!Array.isArray(subInvestorsResponse)) {
      return 0;
    }
    
    // Filter for active and verified sub-investors
    const activeInvestors = subInvestorsResponse.filter(investor => {
      return investor.isActive === true && investor.registrationStatus === 'verified';
    });
    
    return activeInvestors.length;
  }, [subInvestorsResponse]);

  // Calculate active wallets count
  const activeWalletsCount = React.useMemo(() => {
    if (!Array.isArray(walletsResponse)) {
      return 0;
    }
    
    // Filter for active wallets
    const activeWallets = walletsResponse.filter(wallet => {
      return wallet.status === 'active';
    });
    
    return activeWallets.length;
  }, [walletsResponse]);

  // Calculate upcoming distributions count (next 7 days)
  const upcomingDistributionsCount = React.useMemo(() => {
    if (!Array.isArray(paymentsResponse) || paymentsResponse.length === 0) {
      return 0;
    }
    
    // Set current date to start of day (00:00:00)
    const currentDate = new Date();
    currentDate.setHours(0, 0, 0, 0);
    
    // Set next week date to end of day (23:59:59) 7 days from now
    const nextWeekDate = new Date();
    nextWeekDate.setDate(currentDate.getDate() + 7);
    nextWeekDate.setHours(23, 59, 59, 999);
    
    // Filter for payments in the next 7 days
    // Only include payments that are not rejected and have a valid paymentDate
    const upcomingPayments = paymentsResponse.filter(payment => {
      // Skip if payment is rejected
      if (payment.approvalStatus === 'rejected' || payment.paymentStatus === 'Rejected') {
        return false;
      }
      
      // Skip if no paymentDate
      if (!payment.paymentDate) {
        return false;
      }
      
      const paymentDate = new Date(payment.paymentDate);
      
      // Check if date is valid
      if (isNaN(paymentDate.getTime())) {
        return false;
      }
      
      // Set payment date to start of day for comparison
      paymentDate.setHours(0, 0, 0, 0);
      
      return paymentDate >= currentDate && paymentDate <= nextWeekDate;
    });
    
    return upcomingPayments.length;
  }, [paymentsResponse]);

  // Calculate KYC/AML status counts from real data
  const kycStats = React.useMemo(() => {
    if (!Array.isArray(subInvestorsResponse)) {
      return [];
    }
    
    // Count by approval status
    const pendingReviewCount = subInvestorsResponse.filter(inv => 
      inv.approvalStatus === 'pending_l1' || inv.approvalStatus === 'pending_l2'
    ).length;
    
    const approvedCount = subInvestorsResponse.filter(inv => 
      inv.approvalStatus === 'approved'
    ).length;
    
    const rejectedCount = subInvestorsResponse.filter(inv => 
      inv.approvalStatus === 'rejected'
    ).length;
    
    const total = subInvestorsResponse.length;
    
    return [
      { 
        status: 'Pending Review', 
        count: pendingReviewCount, 
        percentage: total > 0 ? Math.round((pendingReviewCount / total) * 100) : 0, 
        color: 'warning' 
      },
      { 
        status: 'Approved', 
        count: approvedCount, 
        percentage: total > 0 ? Math.round((approvedCount / total) * 100) : 0, 
        color: 'success' 
      },
      { 
        status: 'Rejected', 
        count: rejectedCount, 
        percentage: total > 0 ? Math.round((rejectedCount / total) * 100) : 0, 
        color: 'error' 
      }
    ];
  }, [subInvestorsResponse]);

  const isLoading = subInvestorsLoading || walletsLoading || paymentsLoading;

  if (isLoading) {
    return <LoadingSpinner size="lg" text="Loading dashboard..." />;
  }

  const metrics = [
    { name: 'Active Investors', value: activeSubInvestorsCount.toString(), change: 'Verified sub-investors', icon: Users, color: 'success', href: '/custodian/sub-investors' },
    { name: 'Active Wallets', value: activeWalletsCount.toString(), change: 'Custodian wallets', icon: Wallet, color: 'primary', href: '/custodian/wallets' },

    { name: 'Upcoming Distributions', value: upcomingDistributionsCount.toString(), change: 'Next 7 days', icon: FileText, color: 'info', href: '/custodian/coupon-profit-distribution' }
  ];

  const quickActions = [
    { name: 'Review Sub Investors Applications', description: 'Process pending investor verification requests', icon: Users, href: '/custodian/sub-investors', color: 'warning' },
    { name: 'Manage Sub Accounts', description: 'Manage sub-account access and permissions', icon: UserPlus, href: '/custodian/sub-accounts', color: 'primary' },
    { name: 'Manage Sub-Roles', description: 'Create and manage sub-roles with specific permissions', icon: Shield, href: '/custodian/roles', color: 'primary' }
  ];


  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
    
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Custodian Dashboard</h1>
              <p className="mt-1 text-sm text-gray-500">Monitor Sub Investors status, and custody operations</p>
            </div>
          </div>
        </div>
    
      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
       

        {/* Key Metrics */}
        <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            return (
              <Link key={metric.name} to={metric.href} className="transition-shadow card hover:shadow-lg">
                <div className="card-body">
                  <div className="flex items-center">
                    <div className={`flex-shrink-0 p-3 rounded-lg bg-${metric.color}-100`}>
                      <Icon className={`w-6 h-6 text-${metric.color}-600`} />
                    </div>
                    <div className="flex-1 min-w-0 ml-4">
                      <p className="text-sm font-medium truncate text-secondary-600">{metric.name}</p>
                      <p className="text-2xl font-bold">{metric.value}</p>
                      <p className={`text-xs text-${metric.color}-600 mt-1`}>{metric.change}</p>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {/* Quick Actions */}
        <div className="mb-4 card">
          <div className="card-header">
            <h2 className="text-lg font-medium">Quick Actions</h2>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-3 gap-3">
              {quickActions.map((action) => {
                const Icon = action.icon;
                return (
                  <Link 
                    key={action.name} 
                    to={action.href} 
                    className="relative flex flex-col items-center justify-center w-full p-4 text-sm font-medium border rounded btn-primary-hover focus:outline-none focus:ring-2 focus:ring-offset-2"
                  >
                    {action.count && (
                      <span className="absolute inline-flex items-center justify-center px-2 py-1 text-xs font-bold text-white rounded-full -top-2 -right-2 bg-warning-600">
                        {action.count}
                      </span>
                    )}
                    <Icon className="w-6 h-6 mb-2" style={{ color: "var(--color-primary)" }} />
                    <span className="text-center">{action.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 gap-6 mb-4">
          {/* KYC/AML Status */}
          <div className="card">
            <div className="flex items-center justify-between card-header">
              <h3 className="text-lg font-medium">Sub Investors Status</h3>
              <Link to="/custodian/sub-investors" className="text-sm font-medium text-primary-600 hover:text-primary-700">
                Manage All
              </Link>
            </div>
            <div className="card-body">
              <div className="space-y-4">
                {kycStats.map((stat, index) => {
                  // Map color names to Tailwind classes
                  const colorClasses = {
                    warning: 'bg-yellow-500',
                    success: 'bg-green-500',
                    error: 'bg-red-500',
                    '': 'bg-gray-500' // fallback for empty color
                  };
                  const bgColorClass = colorClasses[stat.color] || 'bg-gray-500';
                  
                  return (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${bgColorClass} mr-3`}></div>
                        <span className="text-sm font-medium text-gray-900">{stat.status}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <div className="w-20 h-2 rounded-full bg-secondary-200">
                          <div className={`${bgColorClass} h-2 rounded-full`} style={{ width: `${stat.percentage}%` }} />
                        </div>
                        <span className="w-8 text-sm font-bold text-secondary-900">{stat.count}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

        </div>



      </div>
    </div>
  );
};

export default CustodianDashboard;